import { useBuy } from "@/context/buy";
import { encryptInteger } from "@/Utils/encryptInteger";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa6";
import CheckoutForm from "./CheckoutForm";
import toast from "react-hot-toast";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import { formatLongDateTime, getVenueTimezone } from "@/Utils/dateFormat";
import { DateTime } from "luxon";

export default function BuyTicketModal({ showData, currentArtist = null }) {
  const router = useRouter();
  const {
    openSingleModal,
    setOpenModal,
    setDirectOpen,
    loggedUserDetails,
    setLocationDetails,
  } = useBuy();
  const [artistData, setArtistData] = useState([]);
  const [cartData, setCartData] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [stripeElements, setStripeElements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState("");
  const [data, setData] = useState();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showsAllArtist, setShowsAllArtist] = useState(true);
  const [filteredArtistData, setFilteredArtistData] = useState([]);
  const [isDayPrice, setIsDayPrice] = useState(false);

  const DEBUG_DAYPRICE =
    process.env.NEXT_PUBLIC_DEBUG_DAYPRICE === "true" ||
    process.env.NEXT_PUBLIC_DEBUG_DAYPRICE === "1";

  const formatDateInCustomTimezone = (show_date, show_time) => {
    return formatLongDateTime(show_date, show_time);
  };

  const buyTicket = () => {
    if (totalTickets > 0) {
      let encryptedData = localStorage.getItem("user_data");
      if (!encryptedData) {
        sessionStorage.setItem("last_url", window.location.pathname + window.location.search);
        sessionStorage.setItem("purchase", true);
        setDirectOpen(false);
        sessionStorage.setItem(
          "location_data",
          JSON.stringify({
            city: data?.venue?.city || "",
            state: data?.venue?.state || "",
          })
        );
        sessionStorage.setItem("show_data", JSON.stringify(data));
        router.push("/buy-tickets/details");
        return;
      }

      buyTicketWithUser();
    }
  };

  const addDataInLocalStorage = (type, data) => {
    const isCart = checkCart(data.user_id || data.national_id);
    const isNational = data.national_id ? true : false;
    if (isCart) {
      updateCart(data.user_id || data.national_id, type, isNational);
    } else {
      if (type == "plus") {
        storeData(data, isNational);
      }
    }
  };

  const storeData = (data, isNational) => {
    const cart =
      JSON.parse(localStorage.getItem(`qoncert_cart_${showData?.id}`)) || [];
    const newItem = {
      [isNational ? "national_id" : "artist_id"]: isNational
        ? data.national_id
        : data.user_id,
      user_id: isNational ? data.national_id : data.user_id,
      ticket_count: 1,
      is_national: isNational,
    };
    const updatedCart = [...cart, newItem];
    localStorage.setItem(
      `qoncert_cart_${showData?.id}`,
      JSON.stringify(updatedCart)
    );
    setCartData(updatedCart);
  };

  const checkCart = (id) => {
    const cart =
      JSON.parse(localStorage.getItem(`qoncert_cart_${showData?.id}`)) || [];
    return cart.some((c) => c.user_id == id);
  };

  const updateCart = (id, type, isNational) => {
    const cart =
      JSON.parse(localStorage.getItem(`qoncert_cart_${showData?.id}`)) || [];
    const usrData = cart.find(
      (c) => (isNational ? c.national_id : c.artist_id) == id
    );

    if (!usrData) return;

    const filtered = cart.filter(
      (c) => (isNational ? c.national_id : c.artist_id) !== id
    );

    if (type === "plus") {
      usrData.ticket_count += 1;
    } else if (type === "minus") {
      usrData.ticket_count -= 1;
      if (usrData.ticket_count <= 0) {
        localStorage.setItem(
          `qoncert_cart_${showData?.id}`,
          JSON.stringify(filtered)
        );
        setCartData(filtered);
        return;
      }
    }
    const updatedCart = [...filtered, usrData];
    localStorage.setItem(
      `qoncert_cart_${showData?.id}`,
      JSON.stringify(updatedCart)
    );
    setCartData(updatedCart);
  };

  const fetchShowData = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "getShowDetail");
      formData.append("show_id", showData?.id);

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageShow`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (data.status) {
        const values = data.data;
        setData(values);
        const venueTimezone = getVenueTimezone(values);

        // Prefer show_date + show_time (venue-local), fall back to show_at
        let showAtInVenueTZ = null;
        if (values?.show_date && values?.show_time) {
          showAtInVenueTZ = DateTime.fromFormat(
            `${values.show_date} ${values.show_time}`,
            "yyyy-MM-dd HH:mm:ss",
            { zone: venueTimezone }
          );
        } else if (typeof values?.show_at === "string") {
          const showAt = values.show_at;
          showAtInVenueTZ =
            DateTime.fromISO(showAt, { zone: venueTimezone })?.isValid
              ? DateTime.fromISO(showAt, { zone: venueTimezone })
              : DateTime.fromFormat(showAt, "yyyy-MM-dd HH:mm:ss", { zone: venueTimezone });
        }

        if (showAtInVenueTZ?.isValid) {
          const todayInVenueTZ = DateTime.now().setZone(venueTimezone).toISODate();
          const showDateInVenueTZ = showAtInVenueTZ.toISODate();
          const isDay = todayInVenueTZ === showDateInVenueTZ;

          setIsDayPrice(isDay);
          setPrice(
            isDay ? values?.day_of_show_price ?? values?.ticket_price : values?.ticket_price
          );
          localStorage.removeItem("payment_data");

          if (DEBUG_DAYPRICE) {
            console.log("[buy-ticket-modal][day-price-venue]", {
              todayInVenueTZ,
              showDateInVenueTZ,
              isDayPrice: isDay,
              ticket_price: values?.ticket_price,
              day_of_show_price: values?.day_of_show_price,
              shown_price: isDay
                ? values?.day_of_show_price ?? values?.ticket_price
                : values?.ticket_price,
            });
          }
        } else {
          // Fallback: cannot parse date, use advance price
          setIsDayPrice(false);
          setPrice(values?.ticket_price);
          localStorage.removeItem("payment_data");
        }
      }
    } catch (error) {
      console.error("Error fetching show data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getArtist = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "getArtistsByShow");
      formData.append("show_id", showData.id);

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageShow`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (data) {
        // Combine all artists into one array
        const combinedRaw = [
          ...(data.headLiner || []),
          ...(data.opener || []),
          ...(data.admins || []),
        ];

        // Remove duplicates by user_id or national_id
        const seen = new Set();
        const combined = combinedRaw.filter((item) => {
          const key = item.user_id || item.national_id;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setArtistData(combined);

        if (currentArtist) {
          const filteredData = combined.filter(
            (c) => c.user_id == currentArtist || c.national_id == currentArtist
          );
          setFilteredArtistData(filteredData);
          setShowsAllArtist(false);
        } else {
          setFilteredArtistData(combined);
        }
      }

    } catch (error) {
      console.error("Error fetching artists:", error);
    } finally {
      setLoading(false);
    }
  };

  // payment processing
  const buyTicketWithUser = async (tickets = 0) => {
    try {
      let cartData = JSON.parse(
        localStorage.getItem(`qoncert_cart_${showData?.id}`)
      );
      let referrals = cartData?.map(({ user_id, ...rest }) => rest);
      const totalTickets = cartData.reduce((sum, item) => sum + item.ticket_count, 0);
      let formData = new FormData();
      formData.append("num_tickets", tickets > 0 ? tickets : totalTickets);
      formData.append("show_id", showData?.id);
      formData.append("referrals", JSON.stringify(referrals));
      formData.append("num_tickets", totalTickets);
      formData.append("action", "initiateTB");

      let web_token = loggedUserDetails?.data?.web_token;

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${web_token}`,
          },
        }
      );

      const data = await response.json();
      if (data.status) {
        const temp_data = encryptInteger(JSON.stringify(data.data));
        localStorage.setItem("temp_data", temp_data);
        initiatePayment(data.data);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error in buyTicketWithUser:", error);
    }
  };

  const initiatePayment = async (data) => {
    try {
      let formData = new FormData();
      formData.append("action", "createPI");
      formData.append("reservation_id", data.id);

      let web_token = loggedUserDetails?.data.web_token;

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${web_token}`,
          },
        }
      );

      const resData = await response.json();

      if (resData.status) {
        const encryptedJson = encryptInteger(JSON.stringify(resData.data));
        localStorage.setItem("payment_data", encryptedJson);
        setPaymentLoading(true);
        loadStripePayment(resData.data);
      } else {
        toast.error(resData.message);
      }
    } catch (error) {
      console.error("Error in initiatePayment:", error);
    }
  };

  const loadStripePayment = async (data) => {
    try {
      const stripePromise = await loadStripe(data.publishableKey);

      const options = {
        clientSecret: data.paymentIntent,
        appearance: {
          theme: "night",
          variables: {
            colorPrimary: "#FA2CB4",
            colorBackground: "#0D0C0F",
            colorText: "#FFFFFF",
            colorDanger: "#ff3860",
            borderRadius: "8px",
          },
        },
        layout: {
          type: "tabs",
          defaultCollapsed: false,
        },
      };

      const elements = (
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm />
        </Elements>
      );

      setStripeElements(elements);
      setTimeout(() => {
        setPaymentLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error in loadStripePayment:", error);
    }
  };

  useEffect(() => {
    setLoading(true);

    if (showData?.id) {
      fetchShowData();
      getArtist();

      const cart =
        JSON.parse(localStorage.getItem(`qoncert_cart_${showData.id}`)) || [];

      setCartData(cart);
    }
  }, [showData]);

  useEffect(() => {
    if (price > 0) {
      const totalTickets = cartData.reduce(
        (acc, curr) => acc + curr.ticket_count,
        0
      );
      const totalPrice = cartData.reduce(
        (acc, curr) => acc + curr.ticket_count * price,
        0
      );
      setTotalTickets(totalTickets);
      setTotalPrice(totalPrice);
      let purchase = sessionStorage.getItem("purchase");
      if (purchase) {
        buyTicketWithUser(totalTickets);
        sessionStorage.removeItem("purchase");
      }
    }
  }, [cartData, price]);

  return (
    <div
      id="showDetails"
      className="relative z-[9999]"
      onClick={() => {
        setOpenModal((prev) => ({
          ...prev,
          buy: false,
        }));
        localStorage.removeItem(`qoncert_cart_${showData?.id}`);
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={() =>
          setOpenModal((prev) => ({
            ...prev,
            buy: false,
          }))
        }
      >
        {stripeElements ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="flex justify-center  items-center fixed inset-0  overflow-y-auto bg-gray-700/50 z-[9999999]"
          >
            <div className="bg-[#0D0D10] h-fit w-[90%]  min-h-[300px]  md:mt-[50px] lg:mb-[50px] py-[36px] px-[18px] lg:p-12 rounded-lg md:w-1/3">
              {paymentLoading ? (
                <div className="flex h-[270px] w-full items-center justify-center">
                  <div className="loader"></div>
                </div>
              ) : (
                stripeElements
              )}
            </div>
          </div>
        ) : (
          <div
            className={`flex justify-center backdrop-blur-2xl hide-scrollbar fixed inset-0  overflow-y-auto bg-gray-700/50 z-[99999]`}
          >
            {loading ? (
              <div className="bg-[#0D0C0F] min-h-[100vh] flex justify-center items-center lg:bg-[#1F222A] h-fit w-full  md:mt-[50px] lg:mb-[50px] py-[36px] px-[18px] lg:p-12 rounded-lg md:w-1/3 ">
                <div className="loader"></div>
              </div>
            ) : (
              <div
                id="ticket-modal"
                className="bg-[#17191d] hide-scrollbar min-h-[100vh] relative lg:bg-[#1F222A] h-fit  md:mt-[50px] lg:mb-[50px] py-[36px] px-[18px] lg:py-12 lg:px-9 lg:pb-4 rounded-lg lg:w-[620px] "
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="lg:hidden">
                  <div
                    onClick={() => {
                      setOpenModal((prev) => ({
                        ...prev,
                        buy: false,
                      }));
                    }}
                    className="py-2 px-2 flex items-center gap-4"
                  >
                    <p className="text-[26px]">
                      <FaArrowLeft />
                    </p>
                    <p className="text-2xl font-[700] font-urbanist">
                      Get Tickets
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block">
                  <div className="mt-4 flex items-center justify-between">
                    <h2 className="modal-header">Ticket Summary</h2>
                  </div>
                </div>
                <div className="mt-6 lg:mt-14">
                  <div className="ticket-card">
                    <div className="flex gap-4 h-full">
                      <div className="ticket-main-image min-w-[120px] ">
                        <img src={data?.banner} alt="" />
                      </div>
                      <div className="flex flex-col pt-5 gap-4 h-full">
                        <p className="modal-header urbanist">{data?.name}</p>
                        <p className="modal-date urbanist">
                          {formatDateInCustomTimezone(
                            data?.show_date,
                            data?.show_time
                          )}
                        </p>
                        <div className="modal-location urbanist">
                          <svg
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M2.33301 7.823C2.33301 4.75643 4.8956 2.27783 7.99531 2.27783C11.1038 2.27783 13.6663 4.75643 13.6663 7.823C13.6663 9.36829 13.1043 10.8029 12.1793 12.0189C11.1589 13.3602 9.90111 14.5288 8.48537 15.4461C8.16135 15.6581 7.86892 15.6741 7.51331 15.4461C6.0895 14.5288 4.83173 13.3602 3.82001 12.0189C2.89433 10.8029 2.33301 9.36829 2.33301 7.823ZM6.12916 7.99567C6.12916 9.02297 6.96745 9.83095 7.9953 9.83095C9.02384 9.83095 9.87019 9.02297 9.87019 7.99567C9.87019 6.97636 9.02384 6.12905 7.9953 6.12905C6.96745 6.12905 6.12916 6.97636 6.12916 7.99567Z"
                              fill="white"
                            />
                          </svg>
                          <span className="modal-date">
                            {data?.venue?.name}, {data?.venue?.city},{" "}
                            {data?.venue?.state}
                          </span>
                        </div>
                        {data && (
                          <div className="modal-price urbanist">
                            <p className="text-sm lg:text-base font-[700]">
                              {isDayPrice ? (
                                <>Day Of Show ${data?.day_of_show_price ?? data?.ticket_price}</>
                              ) : (
                                <>Advance ${data?.ticket_price}</>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 lg:mt-11 lg:px-7  flex flex-col gap-7">
                  <p className="modal-header urbanist">How many tickets?</p>
                  <div className="ticket-values bg-[#1F222A]  lg:border-b border-[#373636] rounded-2xl  p-6 flex justify-between urbanist items-center">
                    <p>{totalTickets} Ticket</p>
                    <p>${totalPrice.toLocaleString("en-IN")}.00</p>
                  </div>
                  {filteredArtistData &&
                    filteredArtistData.map((val, idx) => {
                      const ticket =
                        cartData.find((c) =>
                          c.is_national
                            ? c.national_id === val.national_id
                            : c.user_id === val.user_id
                        )?.ticket_count || 0;
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between gap-2 artist-details"
                        >
                          {/* Left Side: Image + Name */}
                          <div
                            onClick={() => {
                              router.push(`/artist/${val.user?.username}`);
                              setOpenModal((prev) => ({
                                ...prev,
                                buy: false,
                              }));
                            }}
                            className="flex items-center gap-2 lg:gap-5 cursor-pointer flex-1 min-w-0"
                          >
                            <div className="usr-img min-w-[60px]">
                              <img
                                src={
                                  val?.national_id
                                    ? val?.national?.photo
                                    : val.user?.profile
                                }
                                alt=""
                              />
                            </div>
                            <div className="usr-name brealAllWords w-[135px] md:w-auto urbanist text-base font-medium">
                              {val?.national_id
                                ? val?.national?.name
                                : val.user?.name}
                            </div>
                          </div>

                          {/* Right Side: Counter */}
                          <div className="indicators min-w-[154px] px-1 flex items-center justify-between">
                            <div
                              className="minus"
                              onClick={() =>
                                addDataInLocalStorage("minus", val)
                              }
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M19 11H5C4.73478 11 4.48043 11.1054 4.29289 11.2929C4.10536 11.4804 4 11.7348 4 12C4 12.2652 4.10536 12.5196 4.29289 12.7071C4.48043 12.8946 4.73478 13 5 13H19C19.2652 13 19.5196 12.8946 19.7071 12.7071C19.8946 12.5196 20 12.2652 20 12C20 11.7348 19.8946 11.4804 19.7071 11.2929C19.5196 11.1054 19.2652 11 19 11Z"
                                  fill="#FA2CB4"
                                />
                              </svg>
                            </div>
                            <p className="urbanist no-selection">{ticket}</p>
                            <div
                              className="plus"
                              onClick={() => addDataInLocalStorage("plus", val)}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M19 11H13V5C13 4.73478 12.8946 4.48043 12.7071 4.29289C12.5196 4.10536 12.2652 4 12 4C11.7348 4 11.4804 4.10536 11.2929 4.29289C11.1054 4.48043 11 4.73478 11 5V11H5C4.73478 11 4.48043 11.1054 4.29289 11.2929C4.10536 11.4804 4 11.7348 4 12C4 12.2652 4.10536 12.5196 4.29289 12.7071C4.48043 12.8946 4.73478 13 5 13H11V19C11 19.2652 11.1054 19.5196 11.2929 19.7071C11.4804 19.8946 11.7348 20 12 20C12.2652 20 12.5196 19.8946 12.7071 19.7071C12.8946 19.5196 13 19.2652 13 19V13H19C19.2652 13 19.5196 12.8946 19.7071 12.7071C19.8946 12.5196 20 12.2652 20 12C20 11.7348 19.8946 11.4804 19.7071 11.2929C19.5196 11.1054 19.2652 11 19 11Z"
                                  fill="#FA2CB4"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  <div className="mt-4  mx-auto">
                    {!showsAllArtist && (
                      <div className=" mb-[22px] py-10 ">
                        <p className=" text-center text-[#9E9E9E] text-sm font-[500] ">
                          Looking for other artists on this show?
                        </p>
                        <p
                          onClick={() => {
                            setShowsAllArtist(true);
                            const sortedArtistData = [
                              ...artistData.filter(
                                (item) => item.user_id === currentArtist
                              ),
                              ...artistData.filter(
                                (item) => item.user_id !== currentArtist
                              ),
                            ];

                            setFilteredArtistData(sortedArtistData);
                          }}
                          className=" mt-[3px] text-center text-[#FA2CB4] font-[700] text-sm cursor-pointer "
                        >
                          View All Artists
                        </p>
                      </div>
                    )}
                    <p className="text-center text-[#9E9E9E] text-sm font-[500] mb-4">
                      All sales are final. No refunds unless the event is canceled.
                    </p>
                    <button
                      onClick={buyTicket}
                      className="w-[320px] main_btn_bg py-3 font-[900] rounded-[16px] no-selection"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
